import { useEffect, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '../redux/hooks'
import { selectChat, setOnSelect, setSelectedUser } from '../redux/reducers/messenger/chatSlice'
import { ChatType } from '../types/webComponent'
import { RecentChat } from '../types/components/chat'
import useMediaQuery from '../utils/useMediaQuery'

/**
 * Auto-opens the first conversation on the Messages page so the user never
 * lands on the empty "Select a chat to start messaging" state.
 *
 * The Messages web component resets chat state (and clears the persisted
 * `selected_user`) on every navigation, so without this the full-page chat
 * always opens with nothing selected. This mirrors the selection a click on the
 * top chat row performs.
 *
 * Guards, in order:
 * - Only for the full-page MESSAGES view (never the sticky CHAT_LIST widget).
 * - Never on mobile: there the list is the landing screen and selecting a chat
 *   hides it, so we keep the list visible and let the user choose.
 * - Deep links win: if the host asked for a specific chat (`selectChatId`, e.g.
 *   from an FCM push), let `useDeepLinkChat` handle it and don't fight it.
 * - Respect any existing selection (restored/deep-linked) — only fill the void.
 *
 * `hasAutoSelectedRef` makes this fire at most once per mount, so the recent
 * chats refetch (which happens on every incoming message) can't keep snapping
 * the user back to the first chat after they've navigated elsewhere.
 */
const useAutoSelectFirstChat = (type?: ChatType, selectChatId?: string | null) => {
  const dispatch = useAppDispatch()
  const { recentChats, recentChatsLoading, selectedUser } = useAppSelector((state) => state.chat)
  const isMobile = useMediaQuery('(max-width: 800px)')
  const hasAutoSelectedRef = useRef(false)

  useEffect(() => {
    if (type !== ChatType.MESSAGES) return
    // A deep link takes precedence; useDeepLinkChat opens that conversation.
    if (selectChatId) return
    if (isMobile) return
    if (hasAutoSelectedRef.current) return
    // Wait for the recent chats to finish loading.
    if (recentChatsLoading) return
    // Something is already open (restored session or deep link) — leave it be.
    if (selectedUser) return
    if (!recentChats?.length) return

    // Pick the first chat the user actually sees at the top of the default list:
    // skip archived and locked chats (a locked chat would immediately prompt for
    // a PIN, which is a poor thing to auto-open into).
    const firstChat = recentChats.find((chat: RecentChat) => !chat.isArchived && !chat.isLocked)
    if (!firstChat) return

    hasAutoSelectedRef.current = true

    dispatch(selectChat({ id: firstChat.chat_id, type: firstChat.chat_type, pinned: firstChat.isPinned }))
    dispatch(setSelectedUser(firstChat))
    dispatch(setOnSelect(true))
  }, [type, selectChatId, isMobile, recentChats, recentChatsLoading, selectedUser, dispatch])
}

export default useAutoSelectFirstChat
